import { Component, Inject, inject, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { PromptSetApiService, PromptConfig, PromptComponents, PromptContent, Example, DoraFieldConfig, FieldType, PromptSet } from '../../shared/api.service';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export interface ImportDialogData {
    /** When provided, prompts are imported into this existing prompt set instead of creating new ones */
    targetPromptSet?: PromptSet;
}

@Component({
    selector: 'app-import-prompt-sets-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressBarModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatSnackBarModule,
    ],
    templateUrl: './import-prompt-sets-dialog.component.html',
    styleUrl: './import-prompt-sets-dialog.component.scss'
})
export class ImportPromptSetsDialogComponent {
    private dialogRef = inject(MatDialogRef<ImportPromptSetsDialogComponent>);
    private promptSetApi = inject(PromptSetApiService);
    private snackBar = inject(MatSnackBar);

    isImporting = false;
    fileName: string | null = null;
    parsedRows: any[] = [];

    /** When set, import goes into this existing prompt set */
    targetPromptSet: PromptSet | null = null;

    constructor(@Optional() @Inject(MAT_DIALOG_DATA) data: ImportDialogData | null) {
        this.targetPromptSet = data?.targetPromptSet || null;
    }

    onFilePicked(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;
        const file = input.files[0];
        this.fileName = file.name;

        // Lazy-load SheetJS to keep bundle lean
        this.parseWithSheetJs(file);
    }

    private async parseWithSheetJs(file: File) {
        try {
            const XLSX = await import('xlsx');
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            this.parsedRows = rows;
            this.snackBar.open(`Loaded ${rows.length} rows`, 'Close', { duration: 2500 });
        } catch (e) {
            console.error(e);
            this.snackBar.open('Failed to parse Excel file', 'Close', { duration: 3500, panelClass: ['error-snackbar'] });
        }
    }

    async startImport() {
        if (!this.parsedRows.length) {
            this.snackBar.open('Please select a valid Excel file first', 'Close', { duration: 2500 });
            return;
        }
        this.isImporting = true;

        try {
            if (this.targetPromptSet) {
                await this.importIntoExistingPromptSet();
            } else {
                await this.importAsNewPromptSets();
            }

            this.snackBar.open('Import completed successfully', 'Close', { duration: 3000, panelClass: ['success-snackbar'] });
            this.dialogRef.close(true);
        } catch (e) {
            console.error(e);
            this.snackBar.open('Import failed. Please check the file format.', 'Close', { duration: 4500, panelClass: ['error-snackbar'] });
        } finally {
            this.isImporting = false;
        }
    }

    /**
     * Import all rows as prompts into the target (existing) prompt set
     * The PromptSetName column is ignored — all prompts go into the target set
     * OutputSchema and SummaryPrompt columns from the first row are also applied
     */
    private async importIntoExistingPromptSet(): Promise<void> {
        const promptSetId = this.targetPromptSet!.prompt_set_id;
        const promptSetName = this.targetPromptSet!.name;
        let firstRow: any = null;

        for (let i = 0; i < this.parsedRows.length; i++) {
            const row = this.parsedRows[i];
            const promptName = (row.PromptName || '').toString().trim();

            // Skip rows without prompt name
            if (!promptName) continue;

            if (!firstRow) {
                firstRow = row;
            }

            await this.importPromptRow(row, promptSetId);
        }

        // Import Output Schema and Summary Prompt from the first data row (if present)
        if (firstRow) {
            await this.importOutputSchema(firstRow, promptSetId, promptSetName);
            await this.importSummaryPrompt(firstRow, promptSetId);
        }
    }

    /**
     * Import rows grouped by PromptSetName, creating new prompt sets for each group
     */
    private async importAsNewPromptSets(): Promise<void> {
        // Group rows by PromptSetName, treating empty PromptSetName as continuation of previous set
        // The first row of each group carries prompt-set-level data (OutputSchema, SummaryPrompt*)
        const grouped = new Map<string, { firstRow: any; promptRows: any[] }>();
        let currentPromptSetName = '';

        for (let i = 0; i < this.parsedRows.length; i++) {
            const row = this.parsedRows[i];
            const promptSetName = row.PromptSetName?.toString().trim();
            const promptName = row.PromptName?.toString().trim();

            // Skip rows without prompt name
            if (!promptName) continue;

            // If PromptSetName is provided, use it as current prompt set
            if (promptSetName) {
                currentPromptSetName = promptSetName;
            }

            // Skip if we don't have a current prompt set name
            if (!currentPromptSetName) continue;

            if (!grouped.has(currentPromptSetName)) {
                grouped.set(currentPromptSetName, { firstRow: row, promptRows: [] });
            }
            grouped.get(currentPromptSetName)!.promptRows.push(row);
        }

        for (const [promptSetName, group] of grouped) {
            const createdSet = await lastValueFrom(this.promptSetApi.createPromptSet({ name: promptSetName }));
            const promptSetId = (createdSet as any)?.promptSetId || createdSet?.prompt_set_id;

            // Import prompts
            for (let i = 0; i < group.promptRows.length; i++) {
                await this.importPromptRow(group.promptRows[i], promptSetId);
            }

            // Import Output Schema from the first row (if present)
            await this.importOutputSchema(group.firstRow, promptSetId, promptSetName);

            // Import Summary Prompt from the first row (if present)
            await this.importSummaryPrompt(group.firstRow, promptSetId);
        }
    }

    /**
     * Import a single prompt row into the given prompt set
     */
    private async importPromptRow(row: any, promptSetId: string): Promise<void> {
        const nowIso = new Date().toISOString();
        const promptName = (row.PromptName || '').toString().trim();
        const identifier = `pid_${uuidv4().replace(/-/g, '')}`;

        // Try both "Prompt" and "Details" columns for backward compatibility
        const promptContent = (row.Prompt || row.Details || '').toString();
        const context = (row.Context || '').toString();

        const content: PromptContent[] = [{ prompt_content: promptContent }];
        const promptComponents: PromptComponents = {
            content,
            context,
            examples: [] as Example[],
            response_format: '' as any,
            use_json_mode: true
        } as any;

        const payload: PromptConfig = {
            prompt_metadata_information: [
                {
                    prompt_set_id: promptSetId,
                    prompt_identifier: identifier,
                    name: promptName,
                    description: promptName,
                    subscription_id: '',
                    createdAt: nowIso
                }
            ],
            prompt_configuration: [
                {
                    promptIdentifier: identifier,
                    prompt: promptComponents,
                    isPrivate: true,
                    validationIdentifier: identifier,
                    createdBy: 'System'
                } as any
            ]
        };

        await lastValueFrom(this.promptSetApi.storePrompt(payload));
    }

    /**
     * Import output schema from the OutputSchema column of the first row of a prompt set group
     * The column value is expected to be a JSON array of field objects
     */
    private async importOutputSchema(firstRow: any, promptSetId: string, promptSetName: string): Promise<void> {
        const outputSchemaStr = (firstRow.OutputSchema || '').toString().trim();
        if (!outputSchemaStr) {
            return;
        }

        try {
            const parsed = JSON.parse(outputSchemaStr);
            const fieldsArray = Array.isArray(parsed) ? parsed : [];

            const fields: DoraFieldConfig[] = fieldsArray.map((item: any) => {
                const field: DoraFieldConfig = {
                    field_name: (item.field_name || '').toString().trim(),
                    field_type: this.parseFieldType((item.field_type || '').toString().trim())
                };

                if (item.default_value != null && item.default_value !== '') {
                    field.default_value = item.default_value;
                }

                if (item.expected_values && Array.isArray(item.expected_values) && item.expected_values.length > 0) {
                    field.expected_values = item.expected_values;
                }

                return field;
            }).filter((f: DoraFieldConfig) => f.field_name); // Skip entries without a field name

            if (fields.length > 0) {
                await lastValueFrom(
                    this.promptSetApi.updateOutputSchema(promptSetId, promptSetName, fields)
                );
            }
        } catch (error) {
            console.error(`Error importing output schema for "${promptSetName}":`, error);
        }
    }

    /**
     * Import summary prompt from the SummaryPrompt* columns of the first row of a prompt set group
     */
    private async importSummaryPrompt(firstRow: any, promptSetId: string): Promise<void> {
        const summaryPromptName = (firstRow.SummaryPromptName || '').toString().trim();
        if (!summaryPromptName) {
            return;
        }

        const context = (firstRow.SummaryPromptContext || '').toString();
        const content = (firstRow.SummaryPromptContent || '').toString();
        const inputFieldsStr = (firstRow.SummaryPromptInputFields || '').toString().trim();
        const selectedInputFields = inputFieldsStr
            ? inputFieldsStr.split(',').map((v: string) => v.trim()).filter((v: string) => v)
            : [];

        const summaryPromptId = `summary_${promptSetId.replace(/-/g, '')}`;

        const promptConfig: PromptConfig = {
            prompt_metadata_information: [{
                prompt_set_id: promptSetId,
                prompt_identifier: summaryPromptId,
                name: summaryPromptName,
                description: 'Summary prompt for aggregating analysis results',
                subscription_id: '',
                metadataDict: {
                    summary_input_fields: selectedInputFields
                }
            }],
            prompt_configuration: [{
                promptIdentifier: summaryPromptId,
                validationIdentifier: summaryPromptId,
                prompt: {
                    context: context,
                    content: [{
                        prompt_content: content,
                        defaultReplacement: [],
                        removeIfNotSet: false
                    }],
                    examples: [],
                    response_format: '',
                    use_json_mode: true
                },
                isPrivate: false
            }]
        };

        try {
            await lastValueFrom(this.promptSetApi.storePrompt(promptConfig));
        } catch (error) {
            console.error(`Error importing summary prompt for prompt set ${promptSetId}:`, error);
        }
    }

    /**
     * Parse a field type string to the FieldType enum value
     * Handles both enum values (str, int) and display labels (Text, Whole Number)
     */
    private parseFieldType(value: string): FieldType {
        const lower = value.toLowerCase();
        switch (lower) {
            case 'str':
            case 'text':
                return FieldType.STR;
            case 'int':
            case 'whole number':
                return FieldType.INT;
            case 'float':
            case 'decimal number':
                return FieldType.FLOAT;
            case 'dict':
            case 'object':
                return FieldType.DICT;
            case 'list':
                return FieldType.LIST;
            default:
                return FieldType.STR;
        }
    }

    clearFile() {
        this.fileName = null;
        this.parsedRows = [];
        // Reset the file input
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    }

    cancel() {
        this.dialogRef.close(false);
    }
}
